import React, { useState, useEffect, useContext, useMemo } from "react";

import SettingsSection from "../../components/section/SettingsSection";
import PageTitle from "../../components/pageTitle/PageTitle";
import PersonalDetails from "../../components/account/personalDetails/personalDetails";
import AccountForm from "../../components/account/accountForm/accountForm";
import { useUser } from "../../context/UserContext";
import { useShopContext } from "../../context/ShopContext";
import { useAppContext } from "../../context/AppContext";

import { UserProfile } from "../../context/types";
import UserFavorites from "../../components/account/userFavorites/userFavorites";
import YourWardrobe from "../../components/yourWardrobe/YourWardrobe";
import Statistics from "../../components/statistics/Statistics";

import { OrderStatusTypes } from "../../commonTypes";
import { fetchPostJSON } from "../../utils/api-helpers";

import styles from "./account.module.css";
import { useRouter } from "next/router";
import { ModalContext } from "../../context/ModalContext";
import OrderCompleteModal from "../../components/modal/orderCompleteModal/orderCompleteModal";
import moment from "moment";
import { findOverlappingOrderVariants } from "../../utils/orderHelpers";
import { updateUserProfile } from "../../modules/@supabase/user";
import DiscountCode from "../../components/discountCode/DiscountCode";
import LoginForm from "../../components/auth/LoginForm";
import { $Enums } from "@prisma/client";
import AccountSubHeader from "../../components/account/accountSubHeader/accountSubheader";
import Button from "../../components/button/Button";
import PaymentIssueNotice from "../../components/paymentIssueNotice/PaymentIssueNotice";
import {
  generateCheckoutSessionId,
  generateSubscriptionSessionId,
} from "../../modules/@stripe";
import getStripe from "../../utils/get-stripejs";

//! temp

const Account = (): JSX.Element => {
  const [edit, setEdit] = useState(false);
  const {
    userProfile,
    refreshUserProfile,
    getUserActiveOrderProducts,
    activeOrderProducts,
    previousOrderProducts,
    upcomingOrderProducts,
    activeOrderSubscriptionPlan,
  } = useUser();
  const [manageSubscriptionLink, setManageSubscriptionLink] = useState();
  const { clearCart, subscriptionOption } = useShopContext();
  const { subscriptions } = useAppContext();
  const { handleModal } = useContext(ModalContext);
  const route = useRouter();
  const [stripeError, setStripeError] = useState("");
  // const [itemsToReturn, setItemsToReturn] = useState([]);
  // const [itemsToKeep, setItemsToKeep] = useState([]);
  // const [newItems, setNewItems] = useState([]);

  const userId = userProfile?.id;
  const firstName = userProfile?.firstName || "";
  const lastName = userProfile?.lastName || "";

  const fromBundlee = userProfile?.role === "Bundlee";

  // checks if there is an active order (wardrobe), requests product list from getUserActiveOrderProducts
  useEffect(() => {
    // if (activeOrderProducts && activeOrderProducts.length === 0) {
    getUserActiveOrderProducts();
    // }
  }, []);

  // const { newItems, itemsToKeep, itemsToReturn } = useMemo(() => {
  //   if (!userProfile?.activeOrder) {
  //     return {
  //       newItems: [],
  //       itemsToKeep: [],
  //       itemsToReturn: [],
  //     };
  //   }

  //   if (previousOrderProducts && previousOrderProducts.length > 0) {
  //     const { overlappingVariants, uniqueVariants } =
  //       findOverlappingOrderVariants(
  //         userProfile.activeOrder,
  //         userProfile.previousOrder
  //       );

  //     return {
  //       newItems: uniqueVariants.map((prodVarId) =>
  //         activeOrderProducts.find(
  //           (prodVar: any) => prodVar.selected_variant.id === prodVarId
  //         )
  //       ),
  //       itemsToKeep: overlappingVariants.map((prodVarId) =>
  //         activeOrderProducts.find(
  //           (prodVar: any) => prodVar.selected_variant.id === prodVarId
  //         )
  //       ),
  //       itemsToReturn: previousOrderProducts.filter(
  //         (orderProd: any) =>
  //           overlappingVariants.indexOf(orderProd.selected_variant.id) < 0 // If the items are not in the overlapping array, then we know they are unique to the old order
  //       ),
  //     };
  //   } else {
  //     // No previous order, so just display the active order
  //     return {
  //       newItems: activeOrderProducts,
  //       itemsToKeep: [],
  //       itemsToReturn: [],
  //     };
  //   }
  // }, [activeOrderProducts, previousOrderProducts, userProfile]);

  const handleEditClick = () => {
    setEdit(!edit);
  };

  useEffect(() => {
    if (route.query.order_complete && userProfile) {
      clearCart();
      handleModal({
        content: <OrderCompleteModal />,
        withImage: true,
      });
    }
    if (route.query.order_complete_renew && userProfile) {
      clearCart();
      handleModal({
        content: (
          <OrderCompleteModal
            title="Rotation submitted"
            copy={
              <p>
                We will collect your current wardrobe at the same time as
                dropping off your new one, you can pack it up in any available
                box or bag ready for us. Thank you for your support.
              </p>
            }
          />
        ),
        withImage: true,
      });
    }
  }, [route.query.order_complete, userProfile]);

  const handleUpdateDetails = async (data: UserProfile) => {
    await updateUserProfile(data, userId).then((response) => {
      refreshUserProfile();
    });
    handleEditClick();
  };

  const getCustomerPortalLink = async () => {
    if (userProfile.stripe_customer_id) {
      const response = await fetchPostJSON("/api/user_plan", {
        customer_id: userProfile.stripe_customer_id,
      });

      if (response.url) {
        setManageSubscriptionLink(response.url);
      }
    }
  };

  const handleSubscribe = async () => {
    if (userProfile.id) {
      const checkoutSesh = await generateSubscriptionSessionId({
        user_id: userProfile.id,
      });

      if ("statusCode" in checkoutSesh && checkoutSesh.statusCode === 500) {
        // Error happened on the backend
        setStripeError(checkoutSesh.message);
      } else if ("id" in checkoutSesh) {
        // Call was successfull
        const stripe = await getStripe();

        //redirects to checkout > order loading state > account page w/ order complete message
        const { error } = await stripe!.redirectToCheckout({
          sessionId: checkoutSesh.id,
        });

        if (error) {
          setStripeError(error.message);
        } else {
          setStripeError("");
        }
      }
    }
  };

  useEffect(() => {
    let timer;
    if (userProfile) {
      getCustomerPortalLink();
    } else {
      timer = setTimeout(() => {
        handleModal({
          content: <LoginForm />,
          withImage: false,
          fullScreen: true,
        });
      }, 1000);
    }

    return () => clearTimeout(timer);
  }, [userProfile]);

  const hasPendingOrder = upcomingOrderProducts.length > 0;
  moment(userProfile?.activeOrder?.delivery_date_time).isAfter();
  const hasReturnOrder = !!userProfile?.activeReturn;

  // userProfile?.previousOrder?.return_date_time &&
  // moment(userProfile?.previousOrder?.return_date_time)
  //   .add(7, "days")
  //   .isAfter();

  return (
    <SettingsSection>
      <PageTitle
        title="My Account"
        noBorder
        subtitle={
          userProfile
            ? `${firstName} ${lastName}`
            : "Edit your account details here"
        }
      />

      {userProfile?.payment_issue_at && (
        <div className={`${styles.container} ${styles.paymentIssue}`}>
          <PaymentIssueNotice />
        </div>
      )}

      {((activeOrderProducts && activeOrderProducts.length > 0) ||
        (previousOrderProducts && previousOrderProducts.length > 0)) && (
        <div className={styles.container}>
          <Statistics
            userProfile={userProfile}
            // returns the selected subscription details
            plan={
              subscriptions?.filter((sub) => {
                if (sub.stripeId === userProfile?.stripe_price_id) {
                  return sub;
                }
              })[0]
            }
          />
        </div>
      )}
      {userProfile && (
        <div className={styles.container}>
          {fromBundlee && !userProfile.stripe_subscription_id ? (
            /**
             * User is from Bundlee and has not activated stripe subscription
             */
            <>
              <AccountSubHeader
                title={"One last step, then you’re up and running"}
              />
              <div>
                <p>
                  We&apos;ve transferred your Bundlee details so you can keep
                  renting with OR Collective.
                </p>
                <p>
                  Click <b>Subscribe</b> to add your card details via Stripe.
                  Once that’s done, your membership will be live.
                </p>

                <div className={styles.buttonContainer}>
                  <Button
                    type="button"
                    text="Subscribe"
                    onClick={handleSubscribe}
                  />
                </div>
                {stripeError && (
                  <div className={styles.error}>{stripeError}</div>
                )}
              </div>
            </>
          ) : (
            <>
              <YourWardrobe
                //@ts-ignore TODO: fix
                deliveryDate={hasPendingOrder && userProfile?.activeOrder?.date}
                wardrobe={
                  hasPendingOrder ? upcomingOrderProducts : activeOrderProducts
                }
                //@ts-ignore TODO: fix
                // returnDate={userProfile?.activeReturn?.date}
                subscriptionCancelled={
                  userProfile?.activeOrder &&
                  userProfile?.activeOrder.status === OrderStatusTypes.FINALISED
                }
              />
              {hasReturnOrder && previousOrderProducts && (
                <>
                  <YourWardrobe
                    //@ts-ignore TODO: fix
                    deliveryDate={userProfile?.activeReturn?.date}
                    wardrobe={previousOrderProducts}
                    exchangedWardrobe
                    returnedItems
                  />
                  {hasPendingOrder && (
                    <YourWardrobe
                      //@ts-ignore TODO: fix
                      deliveryDate={userProfile?.activeReturn?.date}
                      wardrobe={activeOrderProducts}
                      exchangedWardrobe
                      keptItems
                    />
                  )}
                </>
              )}
              {userProfile?.stripe_subscription_id && (
                <DiscountCode
                  subscriptionId={userProfile?.stripe_subscription_id}
                />
              )}
              <UserFavorites />
            </>
          )}

          {edit ? (
            <AccountForm
              handleUpdateDetails={handleUpdateDetails}
              userProfile={userProfile}
              handleEditClick={handleEditClick}
            />
          ) : (
            <PersonalDetails
              userProfile={userProfile}
              handleEditClick={handleEditClick}
              manageSubscriptionLink={manageSubscriptionLink}
            />
          )}
        </div>
      )}
    </SettingsSection>
  );
};

export const getStaticProps = async ({ params }) => {
  // const client = createClient();

  // const page = await client.getSingle("home");

  return {
    props: {},
  };
};

export default Account;
